import { create } from "zustand";
import {
  ORDER_STRATEGIES,
  OrderRelationType,
  OrderUpdateContext,
} from "./orderStrategies";
import useFiltersStore from "./useFiltersStore";

interface IOrder {
  orderHandlers: any[];
  queryOffset: number;
  allOrders: any;
  orderItems: any;
  orderEtas: any;
  orderRemarks: any;
  missedEtaOrderIds: string[];
  alertEtaOrderIds: string[];
  mobileConfigs: any;
  scrollOrderId: any;
  setOrderHandlers: (orderHandlers: any) => void;
  setAllOrders: (allOrders: any[]) => void;
  setSelectedOrderHandler: (
    orderId: any,
    orderHandler: any,
    orderRelationType: OrderRelationType,
    parentId?: string,
  ) => void;
  setOrderItems: (
    allOrders: any,
    orderRelationType: OrderRelationType,
    parentId?: string,
  ) => void;
  setOrderEtas: (
    allOrders: any,
    orderRelationType: OrderRelationType,
    parentId?: string,
  ) => void;
  setOrderRemarks: (
    allOrders: any,
    orderRelationType: OrderRelationType,
    parentId?: string,
  ) => void;
  setOrderRemarksWithOrderId: (orderId: any, orderRemarks: any) => void;
  setOrderItemsWithOrderId: (orderId: any, orderItems: any) => void;
  setOrderEtasWithOrderId: (orderId: any, orderETA: any) => void;
  setOrderMetaInfoWithOrderId: (orderId: any, metaInfo: any) => void;
  setMissedEtaOrderIds: (missedEtaOrderIds: string[]) => void;
  setAlertEtaOrderIds: (alertOrderIds: string[]) => void;
  setQueryOffset: (newQueryOffset: number) => void;
  setScrollOrderId: (orderId: any) => void;
  allParentOrders: any;
  setAllParentOrders: (allParentOrders: any) => void;
  childOrders: Map<string, any[]>;
  setChildOrders: (parentId: string, childOrders: any[]) => void;
  clearAllOrders: () => void;
}

export const useOrdersStore = create<IOrder>((set, get) => ({
  orderHandlers: [],
  allOrders: { orders: [], count: 0 },
  orderItems: {},
  orderRemarks: {},
  queryOffset: 0,
  missedEtaOrderIds: [],
  alertEtaOrderIds: [],
  orderEtas: {},
  mobileConfigs: null,
  scrollOrderId: null,
  childOrders: new Map(),
  allParentOrders: { orders: [], count: 0 },

  setOrderHandlers: (orderHandlers) => {
    set({ orderHandlers });
  },

  setAllOrders: (allOrders) => {
    set({ allOrders });
  },

  setSelectedOrderHandler: (
    orderId,
    orderHandler,
    orderRelationType,
    parentId,
  ) => {
    const strategy = ORDER_STRATEGIES[orderRelationType];
    const context: OrderUpdateContext = {
      orderId,
      data: orderHandler,
      parentId,
    };

    set((state) => strategy.updateOrderHandler(context, state));
  },

  setOrderItems: (allOrders, orderRelationType, parentId) => {
    const strategy = ORDER_STRATEGIES[orderRelationType];

    set((state) => strategy.setOrderItems(allOrders, state, parentId));
  },

  setOrderEtas: (allOrders, orderRelationType, parentId) => {
    const strategy = ORDER_STRATEGIES[orderRelationType];

    set((state) => strategy.setOrderEtas(allOrders, state, parentId));
  },

  setOrderRemarks: (allOrders, orderRelationType, parentId) => {
    const strategy = ORDER_STRATEGIES[orderRelationType];

    set((state) => strategy.setOrderRemarks(allOrders, state, parentId));
  },

  setOrderItemsWithOrderId: (orderId, newOrderItems) => {
    const strategy = ORDER_STRATEGIES[OrderRelationType.DESCENDANT];
    const context: OrderUpdateContext = { orderId, data: newOrderItems };

    set((state) => strategy.updateOrderItems(context, state));
  },

  setOrderEtasWithOrderId: (orderId, newOrderEta) => {
    const strategy = ORDER_STRATEGIES[OrderRelationType.DESCENDANT];
    const context: OrderUpdateContext = { orderId, data: newOrderEta };

    set((state) => strategy.updateOrderEtas(context, state));
  },

  setOrderRemarksWithOrderId: (orderId, newOrderRemarks) => {
    const strategy = ORDER_STRATEGIES[OrderRelationType.DESCENDANT];
    const context: OrderUpdateContext = { orderId, data: newOrderRemarks };

    set((state) => strategy.updateOrderRemarks(context, state));
  },

  setOrderMetaInfoWithOrderId: (orderId, metaInfo) => {
    const strategy = ORDER_STRATEGIES[OrderRelationType.DESCENDANT];
    const context: OrderUpdateContext = { orderId, data: metaInfo };

    set((state) => strategy.updateOrderMetaInfo(context, state));
  },

  setMissedEtaOrderIds: (missedEtaOrderIds: string[]) => {
    set((state) => ({ ...state, missedEtaOrderIds: missedEtaOrderIds }));
  },

  setAlertEtaOrderIds: (alertEtaOrderIds: string[]) => {
    set((state) => ({ ...state, alertEtaOrderIds: alertEtaOrderIds }));
  },

  setQueryOffset: (newQueryOffset: number) => {
    set({ queryOffset: newQueryOffset });
  },

  setScrollOrderId: (orderId) => {
    set((state) => ({ ...state, scrollOrderId: orderId }));
  },

  setAllParentOrders: (allParentOrders) => {
    set({ allParentOrders });
  },

  setChildOrders: (parentId: string, childOrders: any[]) => {
    set((state) => {
      return {
        ...state,
        childOrders: new Map(state.childOrders).set(parentId, childOrders),
      };
    });
  },

  clearAllOrders: () => {
    set({
      allOrders: { orders: [], count: 0 },
      orderItems: {},
      orderEtas: {},
      orderRemarks: {},
      allParentOrders: { orders: [], count: 0 },
      childOrders: new Map(),
    });
  },
}));
